import { useTranslation } from 'react-i18next';

import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { StoreProductLiveState } from '../schemas/live-state';

const STYLES: Record<StoreProductLiveState['kind'], string> = {
  live: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  scheduled: 'bg-primary-tint text-primary border-transparent',
  hidden: 'bg-warning/15 text-warning border-transparent dark:bg-warning/20',
  draft: 'bg-secondary text-secondary-foreground border-transparent',
  unpublished: 'border-border text-muted-foreground',
};

/** "28 Sep" in the store's zone, with the year when it isn't this one. */
function shortDate(format: ReturnType<typeof useFormat>, value: string): string {
  const date = new Date(value);
  const thisYear = new Date().getFullYear() === date.getFullYear();
  return new Intl.DateTimeFormat(format.locale, {
    day: 'numeric',
    month: 'short',
    ...(thisYear ? {} : { year: 'numeric' }),
    timeZone: format.timeZone,
  }).format(date);
}

export interface StoreLiveChipProps {
  state: StoreProductLiveState;
  /** First time it went live here, for "Live since 28 Sep". */
  since?: string | null;
  className?: string;
}

/** One word for what shoppers get: Live, Scheduled 12 Nov, Hidden: master is draft, Unpublished. */
export function StoreLiveChip({ state, since = null, className }: StoreLiveChipProps) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  let label: string;
  let title: string | undefined;
  if (state.kind === 'live') {
    label = since
      ? t('storePage.live.liveSince', { date: shortDate(format, since) })
      : t('storePage.live.live');
    title = since ? format.utc(since) : undefined;
  } else if (state.kind === 'scheduled') {
    label = t('storePage.live.scheduled', { date: shortDate(format, state.at) });
    title = format.utc(state.at);
  } else if (state.kind === 'hidden') {
    label = t(`storePage.live.hidden.${state.master}`);
  } else {
    label = t(`storePage.live.${state.kind}`);
  }
  return (
    <Badge variant="outline" title={title} className={cn(STYLES[state.kind], className)}>
      {label}
    </Badge>
  );
}
